import { fixDoubleHashInUrl, isEmoji, isPdfViewer, stripHtml } from "./Utils";
import { generateFragment } from "./fragment-generation-utils";
export class SelectionUtils {
}
SelectionUtils.getCleanContentFromSelection = (selection, document, location) => {
    var _a, _b;
    let selectionHtml = document.createElement("div");
    selectionHtml.innerHTML = "";
    // Clone DOM nodes from ranges (we support multiselect here)
    for (let i = 0; i < selection.rangeCount; i++) {
        selectionHtml.appendChild(selection.getRangeAt(i).cloneContents());
    }
    let content = selectionHtml.innerHTML.replace(/(class=\".*?\"|id=\".*?\"|style=\".*?\")/g, "");
    // Remove all iframes from the content
    content = content.replace(/<iframe[^>]*>.*?<\/iframe>/gi, "");
    // must check if theres any content in the new card when html is removed
    // else we will be adding empty cards to the database or cards with only html tags
    // also we need to check if the content is an image tag, if it is we will keep it
    const imgTagPresent = /<img[^>]*>/i;
    content =
        stripHtml(content).trim() || imgTagPresent.test(content)
            ? content
            : "";
    // Transforming twemoji (https://github.com/jdecked/twemoji) images to emojis
    // All twemojis have in the alt attribute the original emoji that is represented by the image,
    // we replace the image tag with the appropirate emoji that is extracted from the alt attribute
    let regexp = /<img.*?alt=["|'](.*?)["|'].*?>/g;
    (_a = [...content.matchAll(regexp)]) === null || _a === void 0 ? void 0 : _a.filter((value) => value.length === 2 && isEmoji(value[1])).forEach((match) => {
        content = content.replace(match[0], match[1]);
    });
    //converting all relative urls to absolute ones (images and links)
    let regex = /(src|href)=\s*["']([^"'>]+)["']/g;
    (_b = [...content.matchAll(regex)]) === null || _b === void 0 ? void 0 : _b.forEach((match) => {
        let baseUrl = location.origin;
        let arrtibuteName = match[1];
        let path = match[2];
        //removes images with null or empty src and extract the image from noscript tag (special case for paperpile.com)
        let emptyImageRegex = /<img.*?src="(null| )?".*?>/g;
        let noScriptRegex = /<noscript>(.*?)<\/noscript>/g;
        let emptyImageRegexResult = [...content.matchAll(emptyImageRegex)];
        let noScriptRegexResult = [...content.matchAll(noScriptRegex)];
        if (emptyImageRegexResult.length) {
            emptyImageRegexResult.forEach((emptyImageMatch) => {
                content = content.replace(emptyImageMatch[0], "");
            });
            noScriptRegexResult === null || noScriptRegexResult === void 0 ? void 0 : noScriptRegexResult.forEach((noScriptMatch) => {
                content = content.replace(noScriptMatch[0], noScriptMatch[1]);
            });
        }
        //removes all noscript tags
        noScriptRegexResult === null || noScriptRegexResult === void 0 ? void 0 : noScriptRegexResult.forEach((noScriptMatch) => {
            content = content.replace(noScriptMatch[0], "");
        });
        // converting relative urls to absolute ones
        if (!path.includes("http") && !path.includes("https")) {
            if (path.substring(0, 2) === "//") {
                baseUrl = "https:";
            }
            else if (path.substring(0, 1) === "#" ||
                path.substring(0, 1) !== "/") {
                baseUrl += location.pathname;
            }
            let absoluteURL = `${arrtibuteName}="${baseUrl}${path}"`;
            content = content.replace(match[0], absoluteURL);
        }
    });
    selectionHtml.remove();
    return content;
};
SelectionUtils.getUrlFromSelection = (document, location) => {
    if (isPdfViewer()) {
        return window.location.origin + window.location.pathname;
    }
    let url = "";
    // Explanation can be found here:
    // https://github.com/GoogleChromeLabs/link-to-text-fragment/blob/aa7353ea7424b7e9dea24e827edd27fdef1a1a7b/content_script.js#L42
    let result = generateFragment(window.getSelection());
    window.getSelection().removeAllRanges();
    if (result.status === 0) {
        let fragment = result.fragment;
        let fragmentUrl = `${location.origin}${location.pathname}${location.search}`;
        const prefix = fragment.prefix
            ? `${encodeURIComponent(fragment.prefix)}-,`
            : "";
        const suffix = fragment.suffix
            ? `,-${encodeURIComponent(fragment.suffix)}`
            : "";
        const textStart = encodeURIComponent(fragment.textStart);
        const textEnd = fragment.textEnd
            ? `,${encodeURIComponent(fragment.textEnd)}`
            : "";
        url = `${fragmentUrl}#:~:text=${prefix}${textStart}${textEnd}${suffix}`;
    }
    else {
        url = location.origin + location.pathname;
    }
    url = fixDoubleHashInUrl(url);
    return url;
};
SelectionUtils.getContentFromDocumentDescription = (document) => {
    const DEFAULT_DESCRIPTION = "No description available";
    // Step 1: Check for Schema data (JSON-LD)
    const schemaScript = document.querySelector('script[type="application/ld+json"]');
    if (schemaScript) {
        try {
            const schemaData = JSON.parse(schemaScript.textContent);
            // Check for description in schema data (can be nested in different ways)
            let schemaDescription = null;
            if (Array.isArray(schemaData)) {
                // Sometimes schema data is an array
                for (const item of schemaData) {
                    if (item.description) {
                        schemaDescription = item.description;
                        break;
                    }
                }
            }
            else if (schemaData.description) {
                schemaDescription = schemaData.description;
            }
            else if (schemaData['@graph']) {
                // Sometimes data is in @graph array
                for (const item of schemaData['@graph']) {
                    if (item.description) {
                        schemaDescription = item.description;
                        break;
                    }
                }
            }
            if (schemaDescription && typeof schemaDescription === 'string' && schemaDescription.trim()) {
                // Replace all newlines with HTML line breaks
                schemaDescription = schemaDescription.replace(/\n/g, '<br/>');
                return schemaDescription.trim();
            }
        }
        catch (error) {
            console.warn('Error parsing schema data:', error);
        }
    }
    // Step 2: Check for meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && metaDescription.content && metaDescription.content.trim()) {
        return metaDescription.content.trim();
    }
    // Step 3: Check for open graph description
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription && ogDescription.content && ogDescription.content.trim()) {
        return ogDescription.content.trim();
    }
    // Step 4: Extract from first paragraph
    let contentDiv = document.querySelector('article') ||
        document.querySelector('main') ||
        document.querySelector('[role="main"]') ||
        document.querySelector('.content') ||
        document.querySelector('#content') ||
        document.body;
    if (contentDiv) {
        // Find all paragraph elements within the content
        const paragraphs = contentDiv.querySelectorAll('p');
        const MAX_DESCRIPTION_LENGTH = 1000;
        // Loop through to find the first paragraph that is not empty and not inside js-collabwriting-app
        for (const p of paragraphs) {
            // Skip if this paragraph is inside a js-collabwriting-app container
            if (p.closest('.js-collabwriting-app')) {
                continue;
            }
            const textContent = p.textContent.trim();
            if (textContent.length > 0) {
                // If the paragraph is longer than max length, truncate and add ellipsis
                if (textContent.length > MAX_DESCRIPTION_LENGTH) {
                    return textContent.substring(0, MAX_DESCRIPTION_LENGTH).trim() + '...';
                }
                return textContent;
            }
        }
    }
    // If all else fails, return the message
    return DEFAULT_DESCRIPTION;
};
